import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { isARBleEnabled, isAROrgeEnabled } from '../utils/feature-flags';

import {
  CerealOutcome,
  Country,
  FallowAge,
  FallowType,
  Language,
  ListItem,
  PreviousCrop,
  Soil,
  SoilDepth,
  Specy,
  Tool,
  Variety,
} from '../models/lists.model';

export enum ListType {
  COUNTRIES,
  SPECIES,
  PREVIOUS_CROPS,
  FALLOW_TYPES,
  FALLOW_AGES,
  CEREAL_OUTCOMES,
  SOILS,
  SOIL_DEPTHS,
  VARIETIES,
  TOOLS,
  LANGUAGES,
  DESTINATIONS,
}

// LT est une enum => compilé en number par TypeScript
export abstract class GenericListService<LT extends number> {
  protected listsUrl: string;
  protected cache = new Map<LT, ListItem[]>();

  constructor(protected http: HttpClient) {}

  abstract handleResponse(response: any): Map<LT, ListItem[]>;

  getListTypes(lt: any, ...listTypes: LT[]): Observable<Map<LT, ListItem[]>> {
    // ne récupérer que les listes pas en cache
    const listTypesToFetch = listTypes.filter((x) => !this.cache.has(x));

    if (listTypesToFetch.length === 0) {
      // plus que nécessaire mais ok pour l'appelant
      return of(this.cache);
    }

    // sinon récupérer depuis le serveur
    const qString = listTypesToFetch.map((item) => lt[<number>item]).join(',');
    const source = this.http.get<any>(this.listsUrl + '?types=' + qString);
    const result = source.pipe(
      map((x) => this.handleResponse(x)),
      map((x) => {
        // merger le résultat avec les listes en cache
        for (const listType of listTypes) {
          if (!x.has(listType)) {
            x.set(listType, this.cache.get(listType));
          }
        }
        return x;
      }),
      catchError(this.handleError),
    );
    return <Observable<Map<LT, ListItem[]>>>result;
  }

  protected extractI18n(entity: any): any {
    if (entity.i18n) {
      entity.label = entity.i18n[0].label;
    }
    return entity;
  }

  protected handleError(error: any) {
    // FIXME
    console.error('Erreur lors de la récupération des listes', error);
    return throwError(() => error.message || error);
  }
}

@Injectable({ providedIn: 'root' })
export class ListsService extends GenericListService<ListType> {
  private soilCache = new Map<string, ListItem[]>();

  constructor(http: HttpClient) {
    super(http);
    this.listsUrl = '/lists';
  }

  handleResponse(response: any) {
    const data = response.data;
    const converted = new Map<ListType, ListItem[]>();

    for (const d in data) {
      const listType: ListType = <any>ListType[<any>d];
      let value = data[d];

      if (listType === ListType.TOOLS) {
        // filtrage pour l'accès aux outils ARBlé et AROrge seulement pour les admin
        // et les chefs de secteur
        const showArBle = isARBleEnabled();
        const showArOrge = isAROrgeEnabled();
        value = value.filter((x: ListItem) => (showArBle || x.code !== 'ARBle') && (showArOrge || x.code !== 'AROrge'));
      }

      // pas besoin de extractI18n ça pour varieties
      if (listType !== ListType.VARIETIES) {
        value = value.map(this.extractI18n);
      }

      converted.set(listType, value);
      this.cache.set(listType, value);
    }
    return converted;
  }

  getCountries(includeLanguages = false): Observable<Country[]> {
    // TODO inclure les params dans le cache : mais actuellement est LT => list et utilisé dans de nombreux endroits
    if (includeLanguages) {
      if (this.cache.has(ListType.COUNTRIES)) {
        const countries: Country[] = <Country[]>this.cache.get(ListType.COUNTRIES);
        if (countries.length > 0 && countries[0].languagesGui) {
          // langues sont incluses déjà dans les pays du cache
          return of(countries);
        }
      }

      // ici : soit dans cache mais pas de langues, soit pas dans cache
      const params = '?includeLanguages=true';

      return this.http.get<any>(this.listsUrl + '/' + 'countries' + params).pipe(
        map((response) => {
          const data = response.data.map(this.extractI18n);
          // a plus de données que juste les pays : mais ok pour le reste
          this.cache.set(ListType.COUNTRIES, data);
          return data;
        }),
        catchError(this.handleError),
      );
    } else {
      return <Observable<Country[]>>this.getI18nDataWithCache('countries', ListType.COUNTRIES);
    }
  }

  getSpecies(): Observable<Specy[]> {
    return this.getI18nDataWithCache('species', ListType.SPECIES);
  }

  getSoils(countryCode: string, postalCode: string): Observable<Soil[]> {
    // répète la séquence serveur
    let cacheKey: string;
    let areaCode: string;

    if (countryCode === null || countryCode.length === 0 || postalCode === null || postalCode.length === 0) {
      return of([]);
    }

    if (countryCode === 'FR') {
      // TODO gérer la corse et les DOM-TOM ?
      if (postalCode.length !== 5) {
        return of([]);
      }

      areaCode = postalCode.substr(0, 2);
      if (areaCode === '20') {
        if (postalCode.localeCompare('20200') === -1) {
          // corse du sud : de 20000 à 20190
          areaCode = '2A';
        } else {
          //haute corse : à partir de 20200
          areaCode = '2B';
        }
      } else if (areaCode === '97') {
        // DOM TOM : 3 premières lettres du code postal
        areaCode = postalCode.substring(0, 3);
      }
      cacheKey = countryCode + '_' + areaCode;
    } else {
      areaCode = '_';
      cacheKey = countryCode;
    }

    if (this.soilCache.has(cacheKey)) {
      return of(this.soilCache.get(cacheKey));
    }

    return this.http.get<any>(this.listsUrl + '/soils?country=' + countryCode + '&area=' + areaCode).pipe(
      map((response) => {
        const soils = response.data.map(this.extractI18n);
        this.soilCache.set(cacheKey, soils);
        return soils;
      }),
      catchError(this.handleError),
    );
  }

  getSoilDepths(): Observable<SoilDepth[]> {
    return this.getI18nDataWithCache('soilDepths', ListType.SOIL_DEPTHS);
  }

  getPreviousCrops(): Observable<PreviousCrop[]> {
    return <Observable<PreviousCrop[]>>this.getI18nDataWithCache('previousCrops', ListType.PREVIOUS_CROPS);
  }

  getFallowTypes(): Observable<FallowType[]> {
    return this.getI18nDataWithCache('fallowTypes', ListType.FALLOW_TYPES);
  }

  getFallowAges(): Observable<FallowAge[]> {
    return this.getI18nDataWithCache('fallowAges', ListType.FALLOW_AGES);
  }

  getCerealOutcomes(): Observable<CerealOutcome[]> {
    return this.getI18nDataWithCache('cerealOutcomes', ListType.CEREAL_OUTCOMES);
  }

  getTools(): Observable<Tool[]> {
    return <Observable<Tool[]>>this.getI18nDataWithCache('tools', ListType.TOOLS);
  }

  getLanguages(): Observable<Language[]> {
    return this.getI18nDataWithCache('languages', ListType.LANGUAGES);
  }

  getI18nDataWithCache(url: string, listType: ListType): Observable<ListItem[]> {
    if (this.cache.has(listType)) {
      return of(this.cache.get(listType));
    }

    return this.http.get<any>(this.listsUrl + '/' + url).pipe(
      map((response) => {
        const data = response.data.map(this.extractI18n);
        this.cache.set(listType, data);
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getVarieties(): Observable<Variety[]> {
    if (this.cache.has(ListType.VARIETIES)) {
      return of(<Variety[]>this.cache.get(ListType.VARIETIES));
    }

    return this.http.get<any>(this.listsUrl + '/varieties').pipe(
      map((response) => {
        const varieties = response.data;
        this.cache.set(ListType.VARIETIES, varieties);
        return varieties;
      }),
      catchError(this.handleError),
    );
  }
}
